import React, { useState } from 'react';
import { Box, Paper } from '@mui/material';
import colors from '@/theme/colors';
import { GradePickerDrawer } from './GradePickerDrawer';
import { BodyLargeMidEmphasis, BodySmall } from '@/design-system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Grade } from '@/pages/AccountInformation/AccountInformation.types';

export type GradeField = 'from' | 'to';

interface GradeRangeSelectorProps {
  fromGrade?: string;
  toGrade?: string;
  onChange: (field: GradeField, value?: string) => void;
  grades: Grade[];
}

export const GradeRangeSelector: React.FC<GradeRangeSelectorProps> = ({
  fromGrade,
  toGrade,
  onChange,
  grades,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeField, setActiveField] = useState<GradeField>();

  const handleFieldClick = (field: GradeField) => {
    setActiveField(field);
    setDrawerOpen(true);
  };

  const handleDrawerSelect = (value?: string) => {
    if (activeField) onChange(activeField, value);
  };

  const isActive = (field: GradeField) => drawerOpen && activeField === field;

  const PaperField = ({
    label,
    value,
    field,
  }: {
    label: string;
    value?: string;
    field: GradeField;
  }) => {
    const active = isActive(field);
    const hasValue = value !== undefined && value !== null;

    return (
      <Paper
        onClick={() => handleFieldClick(field)}
        role="button"
        aria-label={`Select grade ${label}`}
        sx={{
          p: '12px',
          pt: 3,
          boxShadow: 'none',
          borderRadius: 2,
          height: 68,
          minWidth: 171,
          cursor: 'pointer',

          border: `${active ? '2px' : '1px'} solid`,
          borderColor: active ? colors.jungle : colors.neutralAsh,
          textAlign: 'left',
          position: 'relative',
          transition: 'border-color 0.2s',
        }}
      >
        <BodySmall
          color={colors.slate}
          sx={{
            position: 'absolute',
            top: hasValue ? 8 : 26,
            left: 12,
            fontSize: hasValue ? 12 : 14,
            color: active ? colors.jungle : colors.neutralAsh,
            transition: 'all 0.2s',
            backgroundColor: 'white',
          }}
        >
          {label}
        </BodySmall>
        <BodyLargeMidEmphasis>{value}</BodyLargeMidEmphasis>
        <ArrowDropDownIcon sx={{ color: colors.prussian, position: 'absolute', right: 12 }} />
      </Paper>
    );
  };

  return (
    <>
      <Box display="flex" gap={2} paddingTop="9px" paddingBottom="9px">
        <PaperField label="From" value={fromGrade} field="from" />
        <PaperField label="To" value={toGrade} field="to" />
      </Box>

      <GradePickerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSelect={handleDrawerSelect}
        initialValue={activeField === 'from' ? fromGrade : toGrade}
        grades={grades}
      />
    </>
  );
};
